import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Container, Icon, List, Row, Column, Badge } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, Video, CTA } from '../../components';
import { useBranchContext } from '../../context/BranchContext';
import { getSessionStorage } from '../../components/utilities/functions';
import { isValidArray } from '../../components/utilities/validations';
import { useLivePreview } from '../../hooks/use-live-preview';

import SocialShare from '../../components/common/social-share';
import CTARibbon from '../../components/common/cta-ribbon';
import FAQ from '../../components/common/faq';
import DynamicComponent from '../../components/common/dynamic-component';
import SDArticle from '../../components/common/structured-data/article';
import CardSection from '../../components/common/card-section';
import LocalNavSidebar from '../../components/common/local-nav-sidebar';
import LocalNavTop from '../../components/common/local-nav-top';
import ShowroomPanel from '../../components/template/branches/showroom-panel';
import ProjectGallery from '../../components/template/articles/project-gallery';
import GalleryModal from '../../components/template/articles/gallery-modal';

import './index.css';

const Articles = (props) => {
	const pageData = useLivePreview(props.data.contentstackArticles, 'articles');
	const authorData = pageData.author_bio?.[0];
	const locations = useSelector((state) => state.locations);
	const { currentBranch } = useBranchContext();
	const [readTime, setReadTime] = useState(null);
	const [articleDates, setArticleDates] = useState({
		created_at: null,
		updated_at: null,
	});
	const [activeShowroom, setActiveShowroom] = useState({});
	const [prevPageInfo, setPrevPageInfo] = useState({ loaded: false });
	const [isGalleryOpen, setIsGalleryOpen] = useState(false);
	const [activeImage, setActiveImage] = useState(null);
	const [galleryImageArray, setGalleryImageArray] = useState([]);
	const isMobile = useMediaQuery({ maxWidth: 767 });

	const isCaseStudy = pageData.article_type?.toLowerCase() === 'case study';
	const isUsingGallery = pageData.article_type?.toLowerCase() !== 'tips and advice';

	useEffect(() => {
		getReadTime();
		convertArticleDates();
		createGalleryArray();

		if (getSessionStorage('previousPage')) {
			setPrevPageInfo({ ...getSessionStorage('previousPage'), loaded: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		if (locations?.showrooms?.[0]) {
			setActiveShowroom(locations.showrooms[0]);
		} else if (locations?.showrooms == null && locations?.showroom) {
			setActiveShowroom(locations.showroom);
		}
	}, [locations.showrooms, locations.showroom]);

	const getReadTime = () => {
		const text = document.getElementById('content')?.innerText;
		const wpm = 225;
		if (text) {
			const words = text.trim().split(/\s+/).length;
			const time = Math.ceil(words / wpm);
			setReadTime(time);
		} else {
			setReadTime(null);
		}
	};

	const convertArticleDates = () => {
		const createdAt = new Date(pageData.date_created);
		const updatedAt = new Date(pageData.updated_at);

		setArticleDates({
			created_at: createdAt?.toLocaleDateString(),
			updated_at: updatedAt?.toLocaleDateString(),
		});
	};

	const createLocalNavArray = () => {
		let parent = null;
		let array = [];

		const contentBlocks = pageData.modular_blocks?.filter((block) => block.content != null);

		for (let i = 0; i < contentBlocks.length; i++) {
			if (contentBlocks[i].content.h2 !== '') {
				// if h2, push to main arr and set parent
				parent = contentBlocks[i].content.h2;
				array.push({
					link: {
						href: `#${contentBlocks[i].content.anchor_text}`,
						title: contentBlocks[i].content.h2,
					},
					childArr: [],
				});
			} else if (contentBlocks[i].content.h3) {
				const parentIndex = array.findIndex((item) => item.link.title === parent);
				array?.[parentIndex]?.childArr?.push({
					link: {
						href: `#${contentBlocks[i].content.anchor_text}`,
						title: contentBlocks[i].content.h3,
					},
				});
			}
		}

		return array;
	};

	const onImageClick = (image) => {
		setActiveImage(image);
		setIsGalleryOpen(true);
	};

	const createGalleryArray = () => {
		let imageArray = [];
		const imageBlocks = pageData.modular_blocks?.filter((block) => block.all_images != null).map((block) => block.all_images);

		for (let i = 0; i < imageBlocks.length; i++) {
			if (!isValidArray(imageBlocks[i].image)) continue;

			if (imageBlocks[i].before_after) {
				imageArray.push(imageBlocks[i].image);
			} else {
				if (imageBlocks[i].image.length > 1) {
					imageBlocks[i].image.forEach((image) => imageArray.push(image));
				} else {
					imageArray.push(imageBlocks[i].image[0]);
				}
			}
		}

		setGalleryImageArray(imageArray);
	};

	const localNavLinks = createLocalNavArray();
	const isStickySidebar = isValidArray(pageData?.branch_data?.[0]?.local_nav) || pageData.rta_component == null ? true : false;

	return (
		<Layout
			id='template-Articles'
			pageTitle={pageData.seo?.title}
		>
			{isValidArray(pageData?.branch_data?.[0]?.local_nav) && (
				<LocalNavTop
					branchData={pageData.branch_data[0]}
					linksArray={pageData?.branch_data?.[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			<Container
				width={1280}
				className='mb-8 xl:mb-12'
			>
				<Row
					className='md:mt-7'
					reverse
				>
					{(isValidArray(localNavLinks) || pageData.rta_component != null) && (
						<Column
							xs={12}
							md={5}
							xl={4}
							className='stickySidebarCol z-10 transition-all duration-500 max-md:sticky max-md:top-0 max-md:max-w-none max-md:flex-[0_0_100vw] max-md:translate-x-3 max-md:bg-white max-md:!px-4'
						>
							<div className={`stickySidebar ${!isStickySidebar ? '' : 'z-2 md:sticky md:transition-all md:duration-500'}`}>
								{!isValidArray(pageData?.branch_data?.[0]?.local_nav) && <LocalNavSidebar linksArr={localNavLinks} />}

								{!isMobile && pageData.rta_component?.includes('truncated') && (
									<ShowroomPanel
										view='truncated'
										showroomData={activeShowroom}
										showFaq={pageData.rta_component?.includes('faq')}
										isCaseStudy={isCaseStudy}
									/>
								)}
							</div>
						</Column>
					)}

					<Column
						xs={12}
						md='fluid'
					>
						{pageData.top_content && (
							<div className={`article-intro ${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''}`}>
								{pageData.top_content.heading && (
									<h1
										{...(pageData.top_content.$?.heading ?? {})}
										className='text-h2 m-0'
									>
										{pageData.top_content.heading}
									</h1>
								)}
								{pageData.top_content.subheading && (
									<div
										className='text-subtitle mt-1'
										{...(pageData.top_content.$?.subheading ?? {})}
										dangerouslySetInnerHTML={{ __html: pageData.top_content.subheading }}
									/>
								)}
							</div>
						)}

						<div className={`article-publishDetails mb-5 mt-4 ${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''}`}>
							<div className={`grid auto-rows-auto items-center gap-x-4 ${isCaseStudy ? 'grid-cols-[24px_1fr_auto]' : 'grid-cols-[60px_1fr_auto]'}`}>
								{isValidArray(authorData?.image) && (
									<Image
										{...(authorData.$?.image__0 ?? {})}
										data={authorData.image[0]}
										className='aspect-square w-full rounded-full'
									/>
								)}

								<div className='flex flex-row flex-wrap items-center'>
									{authorData?.about?.heading && (
										<p className='m-0 flex-12/12 text-base sm:flex-none'>
											By
											<span
												className='ml-1 font-semibold'
												{...authorData.about.$?.heading}
											>
												{(authorData.url && (
													<CTA
														data={{ link: { href: authorData.url } }}
														renderAs='link'
														className='no-underline hover:underline focus:underline'
													>
														{authorData.about.heading}
													</CTA>
												)) ||
													authorData.about.heading}
											</span>
										</p>
									)}

									{!isCaseStudy && (
										<>
											{!pageData.updated_at && articleDates.created_at ? (
												<p
													className='text-body-2 m-0 flex-12/12 sm:flex-none sm:before:px-2 sm:before:text-neutral-200 sm:before:content-["|"]'
													{...(articleDates.$?.created_at ?? {})}
												>
													Published {articleDates.created_at}
												</p>
											) : !pageData.updated_at && pageData.date_created ? (
												<p
													className='text-body-2 m-0 flex-12/12 sm:flex-none sm:before:px-2 sm:before:text-neutral-200 sm:before:content-["|"]'
													{...(pageData.$?.date_created ?? {})}
												>
													Published {pageData.date_created}
												</p>
											) : (
												''
											)}
											{articleDates.updated_at && articleDates.updated_at !== articleDates.created_at && (
												<p
													className='text-body-2 m-0 flex-12/12 sm:flex-none sm:before:px-2 sm:before:text-neutral-200 sm:before:content-["|"]'
													{...(articleDates.$?.updated_at ?? {})}
												>
													Updated {articleDates.updated_at}
												</p>
											)}
											{readTime && <p className='text-body-2 m-0 flex-12/12 sm:flex-none sm:before:px-2 sm:before:text-neutral-200 sm:before:content-["|"]'>{readTime} min read</p>}
										</>
									)}
								</div>

								<div className={`article-socialShare sm:flex sm:justify-end ${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''}`}>
									<SocialShare
										url={pageData.url}
										media={pageData.seo?.thumbnail?.url}
										className='[&_svg]:!mr-0 [&_svg]:ml-2'
									/>
								</div>
							</div>
						</div>

						{isValidArray(pageData.modular_blocks) &&
							pageData.modular_blocks
								.filter((block) => block.all_dynamic == null)
								.map((block, index) => {
									if (isValidArray(block.all_images?.image) && !block.all_images.project_gallery) {
										return (
											<div
												key={index}
												className={`articleImages mx-auto ${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''} ${index === 0 ? '' : 'my-8 max-w-[958px]'}`}
											>
												<div className={`flex items-center justify-center gap-2.5 md:gap-5 ${block.all_images.image_size === 'square-horizontal' ? 'max-md:flex-wrap' : ''}`}>
													{block.all_images.image.map((item, imgIndex) => {
														const WrapperTag = isUsingGallery ? 'button' : 'div';
														return (
															<figure
																key={imgIndex}
																className={`table text-left ${block.all_images.image_size === 'square-horizontal' ? 'flex-12/12 md:flex-initial' : 'flex-initial'}`}
																{...(block.all_images.$?.[`image__${index}`] ?? {})}
															>
																<WrapperTag
																	className={`group relative ${isUsingGallery ? 'hover:before:opacity-100' : ''} before:absolute before:left-0 before:right-0 before:z-2 before:h-full before:w-full before:bg-gradient-to-t before:from-black/70 before:to-transparent before:opacity-0 before:transition-opacity before:content-[""] hover:shadow-[0px_2px_4px_rgba(0,0,0,0.05)]`}
																	onClick={isUsingGallery ? () => onImageClick(item?.image?.url) : null}
																>
																	{block.all_images.before_after && (
																		<Badge
																			color='black'
																			className='absolute left-1 top-1 z-3 md:left-2 md:top-2 xl:left-3 xl:top-3'
																		>
																			{imgIndex === 0 ? 'Before' : 'After'}
																		</Badge>
																	)}

																	<Image
																		data={item}
																		className='max-w-full'
																	/>

																	{isUsingGallery && (
																		<span className={`${isUsingGallery ? 'group-hover:opacity-100' : ''} text-button absolute bottom-4 right-4 z-3 flex items-center gap-x-2.5 text-white opacity-0 transition-opacity`}>
																			<Icon
																				name='zoom_out_map'
																				size={16}
																			/>
																			<span className='underline decoration-white decoration-2 underline-offset-4'>Expand Image</span>
																		</span>
																	)}
																</WrapperTag>

																{item.caption && (
																	<figcaption
																		className='mt-1 table-caption caption-bottom text-base font-semibold'
																		{...(item.$?.caption ?? {})}
																		dangerouslySetInnerHTML={{ __html: item.caption }}
																	/>
																)}
															</figure>
														);
													})}
												</div>
											</div>
										);
									}

									if (block.content != null && !block.content.is_quote) {
										return (
											<div
												id={block.content.anchor_text}
												key={index}
												className={`my-8 ${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''}`}
											>
												{block.content.h2 && (
													<h2
														{...(block.content.$?.h2 ?? {})}
														dangerouslySetInnerHTML={{ __html: block.content.h2 }}
													/>
												)}
												{block.content.h3 && (
													<h3
														{...(block.content.$?.h3 ?? {})}
														dangerouslySetInnerHTML={{ __html: block.content.h3 }}
													/>
												)}

												{block.content.description && (
													<div
														className='[&_h3]:mt-5 [&_h4]:mt-5'
														{...(block.content.$?.description ?? {})}
														dangerouslySetInnerHTML={{ __html: block.content.description }}
													/>
												)}
											</div>
										);
									}

									if (block.content != null && block.content.is_quote) {
										return (
											<div
												key={index}
												id={block.content.anchor_text}
												className={`${isCaseStudy ? 'mx-auto md:max-w-screen-sm xl:max-w-screen-md' : ''}`}
											>
												{block.content.description && (
													<blockquote
														className='!py-7 [&_cite]:!mb-0 [&_cite]:!mt-2'
														{...(block.content.$?.description ?? {})}
														dangerouslySetInnerHTML={{ __html: block.content.description }}
													/>
												)}
											</div>
										);
									}

									if (isValidArray(block.cta_ribbon?.cta_ribbon)) {
										return (
											<div
												className={`my-7 md:my-12 [&:last-child]:mb-0 ${isCaseStudy ? 'mx-auto max-w-screen-sm xl:max-w-screen-md' : ''}`}
												key={index}
											>
												<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
											</div>
										);
									}

									if (isValidArray(block.project_details?.details)) {
										return (
											<div
												key={index}
												className='my-6'
											>
												<div className='bg-neutral-50 px-4 py-7 md:px-10 md:py-6'>
													{block.project_details.heading && (
														<p
															className='text-overline mb-5 leading-normal'
															{...(block.project_details.$?.heading ?? {})}
														>
															{block.project_details.heading}
														</p>
													)}

													<List
														bullets='none'
														className='flex flex-row flex-wrap gap-y-5 md:gap-x-12 md:gap-y-6'
													>
														{block.project_details.details.map((item, index) => (
															<li
																key={index}
																className='max-w-full flex-12/12 md:flex-none'
																{...(pageData.$?.[`modular_blocks__${index}`] ?? {})}
															>
																{item.subheading && (
																	<p
																		{...(item.$?.subheading ?? {})}
																		className='text-sm font-semibold tracking-[0.1px] text-black'
																	>
																		{item.subheading}
																	</p>
																)}
																{item.description && (
																	<div
																		className='text-body-2'
																		{...(item.$?.description ?? {})}
																		dangerouslySetInnerHTML={{ __html: item.description }}
																	/>
																)}
															</li>
														))}
													</List>
												</div>
											</div>
										);
									}

									if (isValidArray(block.faq?.faqs)) {
										return (
											<div
												key={index}
												className='my-7 md:my-12'
											>
												{block.faq.heading && <h3 {...(block.faq.$?.heading ?? {})}>{block.faq.heading}</h3>}

												<FAQ data={block.faq.faqs} />

												{isValidArray(block.faq.ctas) && (
													<CTA
														data={block.faq.ctas[0]}
														className='mt-4 inline-block font-semibold'
													/>
												)}
											</div>
										);
									}

									if (isValidArray(block.video?.video)) {
										return (
											<div
												key={index}
												className='my-7 md:mx-auto md:my-12 md:max-w-screen-sm xl:max-w-screen-md'
											>
												{block.video.heading && <h2 {...(block.video.$?.heading ?? {})}>{block.video.heading}</h2>}
												<Video data={block.video.video[0]} />
											</div>
										);
									}

									if (block.cards != null && !isCaseStudy) {
										return (
											<CardSection
												data={block.cards}
												key={index}
											/>
										);
									}
								})}
					</Column>
				</Row>

				{isMobile && pageData.rta_component?.includes('truncated') && (
					<ShowroomPanel
						view='truncated'
						showroomData={activeShowroom}
						showFaq={pageData.rta_component?.includes('faq')}
						isCaseStudy={isCaseStudy}
					/>
				)}
			</Container>

			{pageData.rta_component?.includes('panel') && (
				<ShowroomPanel
					view='panel'
					showroomData={activeShowroom}
					showFaq={pageData.rta_component?.includes('faq')}
					isCaseStudy={isCaseStudy}
				/>
			)}

			{isUsingGallery && isValidArray(galleryImageArray) && (
				<GalleryModal
					images={galleryImageArray}
					isOpen={isGalleryOpen}
					activeImage={activeImage}
					onClose={() => {
						setIsGalleryOpen(false);
						setActiveImage(null);
					}}
					articleTitle={pageData.top_content?.heading}
				/>
			)}

			{/* Move cards/dynamic outside of container? */}
			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks
					.filter((block) => block.all_dynamic != null || (block.cards != null && isCaseStudy) || block.all_images?.project_gallery || block.feature != null)
					?.map((block, index) => {
						const isProject = pageData.taxonomies?.some((term) => ['window_projects', 'patio_door_projects', 'front_door_projects'].includes(term.term_uid));
						const isBranchProject = pageData.taxonomies?.find((term) => term.term_uid === 'branch_project') ? true : false;
						const isSameBranch = prevPageInfo?.prevUrl?.includes(pageData.branch_data?.[0]?.branch_url);
						const showBranchProjects = prevPageInfo?.loaded && prevPageInfo?.branchReferrer && ((isProject && !isBranchProject) || (isBranchProject && isSameBranch)) && currentBranch?.tag_name ? true : false;
						const showGlobalProjects = prevPageInfo?.loaded && !prevPageInfo?.branchReferrer && isProject;

						if (block.all_dynamic != null && (isCaseStudy || (!showBranchProjects && !showGlobalProjects))) {
							return (
								<DynamicComponent
									key={index}
									data={block.all_dynamic}
									parentURL={pageData.url}
									className='lg:!pt-8'
								/>
							);
						}

						if (block.all_dynamic != null && !isCaseStudy && showBranchProjects && !showGlobalProjects) {
							const productTypeTags = pageData.taxonomies?.filter((taxonomy) => ['window_projects', 'patio_door_projects', 'front_door_projects'].includes(taxonomy.term_uid)).map((term) => term.term_uid);
							const dynamicCta = isValidArray(block.all_dynamic.dynamic?.[0]?.ctas)
								? [
										{
											...block.all_dynamic.dynamic[0].ctas[0],
											link: { href: `${currentBranch?.branch_url}projects/`, title: 'View All Projects' },
										},
									]
								: [];

							return (
								<DynamicComponent
									key={index}
									data={{
										...block.all_dynamic,
										heading: currentBranch?.formal_name ? `More Projects in ${currentBranch.formal_name.split('Pella Windows & Doors of ')?.pop()}` : `More Projects`,
										dynamic: [
											{
												...block.all_dynamic.dynamic[0],
												filter_tags: [...productTypeTags, currentBranch?.tag_name?.toLowerCase(), 'residential_project', 'branch_project'],
												ctas: dynamicCta,
											},
										],
									}}
									parentURL={pageData.url}
									className='lg:!pt-8'
								/>
							);
						}

						if (block.all_dynamic != null && !isCaseStudy && !showBranchProjects && showGlobalProjects) {
							const productTypeCta = prevPageInfo?.productType === 'Window' ? '/ideas/windows/before-after/' : prevPageInfo?.productType === 'Patio Door' ? '/ideas/doors/patio-doors/before-after/' : prevPageInfo?.productType === 'Entry Door' ? '/ideas/doors/entry-doors/before-after/' : '';
							const productTypeTags = pageData.taxonomies?.filter((taxonomy) => ['window_projects', 'patio_door_projects', 'front_door_projects'].includes(taxonomy.term_uid)).map((term) => term.term_uid);

							const dynamicCta =
								isValidArray(block.all_dynamic.dynamic?.[0]?.ctas) && productTypeCta
									? [
											{
												...block.all_dynamic.dynamic[0].ctas[0],
												link: { href: productTypeCta, title: `View More ${prevPageInfo?.productType} Projects` },
											},
										]
									: [];

							return (
								<DynamicComponent
									key={index}
									data={{
										...block.all_dynamic,
										heading: `Featured ${prevPageInfo?.productType || ''} Projects`,
										dynamic: [
											{
												...block.all_dynamic.dynamic[0],
												filter_tags: [...productTypeTags, 'residential_project'],
												ctas: dynamicCta,
											},
										],
									}}
									parentURL={pageData.url}
									className='lg:!pt-8'
								/>
							);
						}

						if (block.cards != null) {
							return (
								<CardSection
									data={block.cards}
									key={index}
								/>
							);
						}

						if (block.all_images != null && isUsingGallery && isValidArray(galleryImageArray)) {
							return (
								<ProjectGallery
									key={index}
									heading={block.all_images.heading}
									images={galleryImageArray}
									onImageClick={onImageClick}
								/>
							);
						}

						if (block.feature != null) {
							return (
								<Container key={index}>
									<div className='articleFeatures py-8 md:py-10 xl:py-12'>
										{block.feature.heading && (
											<h2
												{...(block.feature.$?.heading ?? {})}
												className='mb-6 text-center'
											>
												{block.feature.heading}
											</h2>
										)}

										{isValidArray(block.feature.group) && (
											<Row
												className='!-mx-2'
												justify='center'
												verticalGutters
											>
												{block.feature.group.map((group, index) => (
													<Column
														key={index}
														xs={12}
														md={3}
														className='!px-2'
													>
														<div className='h-full border border-neutral-100 bg-white px-4 py-6'>
															{group.heading && (
																<p
																	className='text-subtitle mb-3 font-bold'
																	{...(group.$?.heading ?? {})}
																>
																	{group.heading}
																</p>
															)}
															{group.description && (
																<div
																	{...(group.$?.description ?? {})}
																	dangerouslySetInnerHTML={{ __html: group.description }}
																/>
															)}
														</div>
													</Column>
												))}
											</Row>
										)}
									</div>
								</Container>
							);
						}
					})}

			{pageData && authorData?.about?.heading && (
				<SDArticle
					headline={pageData.top_content.heading}
					description={pageData.top_content.subheading || pageData.modular_blocks.filter((item) => item.content != null)?.[0]?.content?.description}
					image={pageData.modular_blocks.filter((item) => item.all_images != null)?.[0]?.all_images?.image?.[0]?.image?.url}
					author={authorData.about.heading}
					datePublished={pageData.date_created}
					dateModified={pageData.updated_at}
					url={process.env.GATSBY_SITE_URL + pageData.url}
					authorUrl={authorData.url ? process.env.GATSBY_SITE_URL + authorData.url : null}
				/>
			)}
		</Layout>
	);
};

Articles.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Articles;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- gatsby component
		{...data.contentstackArticles.seo}
		// eslint-disable-next-line react/prop-types -- gatsby component
		image={data.contentstackArticles.seo.thumbnail?.url}
		type='article'
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackArticles(id: { eq: $uid }) {
			__typename
			uid
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			article_type
			rta_component
			branch_id
			branch_data {
				...BranchData
			}
			date_created
			top_content {
				heading
				subheading
			}
			author_bio {
				...Author
			}
			taxonomies {
				term_uid
			}
			modular_blocks {
				all_images {
					heading
					project_gallery
					before_after
					image_size
					image {
						...Image
						image {
							gatsbyImageData(quality: 90)
						}
					}
				}
				project_details {
					heading
					details {
						subheading
						description
					}
					aside_image {
						...Image
						image {
							gatsbyImageData(quality: 90)
						}
					}
				}
				content {
					anchor_text
					h2
					h3
					description
					is_quote
				}
				cta_ribbon {
					heading
					cta_ribbon {
						...CTARibbon
					}
				}
				all_dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				video {
					heading
					video {
						...Video
					}
				}
				faq {
					heading
					faqs {
						question
						answer
					}
					ctas {
						...CTA
					}
				}
				cards {
					...CardSection
				}
				feature {
					heading
					group {
						heading
						description
					}
				}
			}
			seo {
				...SEO
				thumbnail {
					url
				}
			}
			created_at
			updated_at
		}
	}
`;
